// CommandPalette — the global Cmd-K command palette overlay (RFC-0011 Task 2.2).
//
// Composes the Phase-1 primitives: a debounced search input (useSearch), the
// RepoScopeToggle as the scope chip, a listbox of SearchResultItem rows, and a
// "See all results →" affordance that hands off to the `/search` page carrying
// the current scope. Reads open/close + the route-resolved initial scope from
// useCommandPalette(); renders nothing when closed.
//
// Accessibility (the critical part):
//   - `role="combobox"` input + `role="listbox"` results + `role="option"` rows,
//     with `aria-activedescendant` tracking the active row (combobox pattern —
//     focus stays on the input; the list is navigated with the arrow keys).
//   - Arrow Up/Down move the active row (clamped, no wrap); Enter selects; Esc
//     closes. Mouse hover syncs the active row.
//   - Focus trap while open + focus restore to the previously-focused element on
//     close; click-outside (backdrop) closes.
//
// Security: bespoke palette — NO new runtime dependency (no cmdk/kbar), no eval,
// no inline scripts, no `dangerouslySetInnerHTML`. Snippet highlighting is handled
// inside SearchResultItem via the pure highlight parser; same-origin `/api/search`
// only (CSP `connect-src 'self'`).

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';
import type { SearchHit } from '@portal/shared-types';
import { useCommandPalette, type SearchScope } from '@/context/command-palette';
import { useSearch } from '@/hooks/use-search';
import { useAuth } from '@/context/auth';
import { useFavorites } from '@/lib/queries/favorites';
import { useRecentlyViewed } from '@/lib/queries/recently-viewed';
import { RepoScopeToggle } from '@/components/search/repo-scope-toggle';
import { SearchResultItem } from '@/components/search/search-result-item';
import { Skeleton } from '@/components/ui/query-skeletons';

/** Palette result cap — the full set lives on `/search` behind "See all results". */
const PALETTE_PER_PAGE = 10;
/** Max items per idle section (Favorites / Recently-viewed) — RFC-0018 §5. */
const IDLE_SECTION_CAP = 8;
const LISTBOX_ID = 'command-palette-listbox';
const optionId = (index: number) => `command-palette-option-${index}`;

/** A flat, navigable idle-state quick-link (a favorite or a recently-viewed doc). */
interface IdleItem {
  key: string;
  repo: string;
  path: string;
  title: string | null;
}

/** Focusable elements for the focus trap (result rows are combobox options, not tab stops). */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Build the RFC-0010 doc-reader route from a hit's own repo/path (never a raw URL). */
function docHref(repo: string, path: string): string {
  return `/projects/${encodeURIComponent(repo)}/docs/${path}`;
}

/**
 * Mount gate: the dialog (with its state, effects, and useSearch) only exists
 * while the palette is open, so focus capture/restore happens on mount/unmount
 * and no request is scheduled while closed.
 */
export function CommandPalette() {
  const { isOpen } = useCommandPalette();
  if (!isOpen) return null;
  return <CommandPaletteDialog />;
}

function CommandPaletteDialog() {
  const { close, initialScope, repoName } = useCommandPalette();
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [scope, setScope] = useState<SearchScope>(initialScope);
  const [activeIndex, setActiveIndex] = useState(0);

  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  const trimmed = q.trim();
  const result = useSearch({
    q,
    repo: scope === 'repo' ? repoName : undefined,
    page: 1,
    perPage: PALETTE_PER_PAGE,
  });

  const hits: SearchHit[] = useMemo(() => result.data?.data ?? [], [result.data]);
  const total = result.data?.pagination.total ?? 0;

  // Display states (RFC §4). Idle short-circuits before any request is scheduled.
  const showIdle = trimmed.length === 0;
  const showLoading = !showIdle && result.isLoading;
  const showError = !showIdle && result.isError;
  const showResults = !showIdle && !showLoading && !showError && hits.length > 0;
  const showNoResults =
    !showIdle && !showLoading && !showError && result.data !== undefined && hits.length === 0;
  const showSeeAll = showResults && total > hits.length;

  // RFC-0018: the Cmd-K empty state. These hooks live INSIDE the dialog — which only
  // mounts while the palette is open — and are `enabled: isAuthenticated`, so they
  // fire ONLY while the palette is open AND the user is authenticated (never on a
  // closed palette, never anonymously).
  const { isAuthenticated } = useAuth();
  const favoritesQuery = useFavorites();
  const recentsQuery = useRecentlyViewed();
  const favTop = useMemo(
    () => (favoritesQuery.data ?? []).slice(0, IDLE_SECTION_CAP),
    [favoritesQuery.data],
  );
  const recTop = useMemo(
    () => (recentsQuery.data ?? []).slice(0, IDLE_SECTION_CAP),
    [recentsQuery.data],
  );
  // Flat, ordered list (favorites then recents) for keyboard navigation. Section-
  // prefixed keys since the same doc can appear in both sections.
  const idleItems = useMemo<IdleItem[]>(() => {
    if (!showIdle || !isAuthenticated) return [];
    return [
      ...favTop.map((f) => ({ key: `fav:${f.repo}:${f.path}`, repo: f.repo, path: f.path, title: f.title })),
      ...recTop.map((r) => ({ key: `rec:${r.repo}:${r.path}`, repo: r.repo, path: r.path, title: r.title })),
    ];
  }, [showIdle, isAuthenticated, favTop, recTop]);

  // Idle-state branches: the quick-link sections when there is anything to show; the
  // static hint only when both sections are empty (and not still loading, to avoid a
  // hint→links flash for a returning user); nothing while the idle queries load.
  const idleLoading =
    showIdle && isAuthenticated && (favoritesQuery.isLoading || recentsQuery.isLoading);
  const showIdleLinks = showIdle && idleItems.length > 0;
  const showIdleHint = showIdle && !showIdleLinks && !idleLoading;

  // Keep the active row in range as results change; reset when the query/scope changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [trimmed, scope]);
  // The currently-navigable list is the search hits (query mode) or the idle links.
  const navCount = showResults ? hits.length : showIdleLinks ? idleItems.length : 0;
  const clampedActive = navCount > 0 ? Math.min(activeIndex, navCount - 1) : 0;

  // Capture the invoking element on open; focus the input; restore focus on close.
  useEffect(() => {
    restoreRef.current = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => {
      restoreRef.current?.focus?.();
    };
  }, []);

  // Body scroll-lock while the dialog is mounted (it only mounts while open),
  // restoring the previous value on every close path — mirrors the Drawer
  // primitive so the page can't scroll behind the palette on a phone.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  function selectDoc(repo: string, path: string) {
    close();
    navigate(docHref(repo, path));
  }

  function selectHit(hit: SearchHit) {
    selectDoc(hit.repo, hit.path);
  }

  function goToSearchPage() {
    const params = new URLSearchParams({ q: trimmed });
    if (scope === 'repo' && repoName) params.set('repo', repoName);
    close();
    navigate(`/search?${params.toString()}`);
  }

  function onDialogKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    // Focus trap: keep Tab within the dialog.
    if (event.key === 'Tab') {
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeEl = document.activeElement;
      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    // Arrow/Enter navigation applies to whichever list is showing — the search hits
    // (query mode) or the idle favorites/recents quick-links.
    if (!showResults && !showIdleLinks) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, navCount - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (showResults) {
        const hit = hits[clampedActive];
        if (hit) selectHit(hit);
      } else {
        const item = idleItems[clampedActive];
        if (item) selectDoc(item.repo, item.path);
      }
    }
  }

  const activeDescendant =
    showResults || showIdleLinks ? optionId(clampedActive) : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-3 pt-[10vh] sm:p-4 sm:pt-[12vh]"
      role="presentation"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-(--c-shadow-md) sm:max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onDialogKeyDown}
      >
        {/* Input row: combobox + scope chip. */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <MagnifyingGlass size={18} className="shrink-0 text-muted" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label="Search all projects"
            aria-expanded={showResults || showNoResults || showIdleLinks}
            aria-controls={LISTBOX_ID}
            aria-autocomplete="list"
            aria-activedescendant={activeDescendant}
            value={q}
            placeholder={
              scope === 'repo' && repoName ? `Search ${repoName}…` : 'Search all projects…'
            }
            onChange={(e) => setQ(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-sm text-text outline-none placeholder:text-muted"
          />
          <RepoScopeToggle
            scope={scope}
            repoName={repoName}
            onChange={(next) => setScope(next)}
            className="shrink-0"
          />
        </div>

        {/* Results / states. */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2">
          {showIdleHint && (
            <p data-testid="command-palette-idle" className="px-3 py-6 text-center text-xs text-muted">
              Search across all your project docs.
            </p>
          )}

          {/* RFC-0018: the idle empty state — Favorites (≤8, newest-starred first)
              then Recently-viewed (≤8, most-recent first). An empty section renders
              nothing; both-empty falls back to the hint above. One listbox so the
              existing arrow/Enter + aria-activedescendant model navigates it. */}
          {showIdleLinks && (
            <ul
              id={LISTBOX_ID}
              role="listbox"
              aria-label="Favorites and recently viewed"
              className="flex flex-col gap-0.5"
            >
              {favTop.length > 0 && (
                <li
                  role="presentation"
                  className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted"
                >
                  Favorites
                </li>
              )}
              {favTop.map((f, i) => (
                <IdleOption
                  key={`fav:${f.repo}:${f.path}`}
                  index={i}
                  active={i === clampedActive}
                  title={f.title}
                  repo={f.repo}
                  path={f.path}
                  onActivate={() => setActiveIndex(i)}
                  onSelect={() => selectDoc(f.repo, f.path)}
                />
              ))}

              {recTop.length > 0 && (
                <li
                  role="presentation"
                  className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted"
                >
                  Recently viewed
                </li>
              )}
              {recTop.map((r, j) => {
                const index = favTop.length + j;
                return (
                  <IdleOption
                    key={`rec:${r.repo}:${r.path}`}
                    index={index}
                    active={index === clampedActive}
                    title={r.title}
                    repo={r.repo}
                    path={r.path}
                    onActivate={() => setActiveIndex(index)}
                    onSelect={() => selectDoc(r.repo, r.path)}
                  />
                );
              })}
            </ul>
          )}

          {showLoading && (
            <div role="status" aria-label="Searching" className="flex flex-col gap-2 p-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-lg px-3 py-2">
                  <Skeleton className="mb-2 h-4 w-1/2" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              ))}
            </div>
          )}

          {showError && (
            <p role="status" className="px-3 py-6 text-center text-sm text-muted">
              Search is temporarily unavailable. Please try again in a moment.
            </p>
          )}

          {showNoResults && (
            <div className="px-3 py-6 text-center text-sm text-muted">
              <p className="mb-2">No results for &ldquo;{trimmed}&rdquo;.</p>
              <button
                type="button"
                onClick={goToSearchPage}
                className="text-accent hover:underline"
              >
                Search the full portal &rarr;
              </button>
            </div>
          )}

          {showResults && (
            <ul id={LISTBOX_ID} role="listbox" aria-label="Search results" className="flex flex-col gap-0.5">
              {hits.map((hit, i) => (
                <li
                  key={hit.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === clampedActive}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <SearchResultItem
                    result={hit}
                    isActive={i === clampedActive}
                    onSelect={selectHit}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* See-all footer — carries the current query + scope to /search. */}
        {showSeeAll && (
          <div className="border-t border-border p-2">
            <button
              type="button"
              onClick={goToSearchPage}
              className="w-full rounded-lg px-3 py-2 text-left text-xs font-medium text-accent hover:bg-accent-faint"
            >
              See all {total} results &rarr;
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One idle-state quick-link row (a favorite or a recently-viewed doc) — a
 * `role="option"` matching the search-results listbox model: `aria-selected`
 * tracks the active row, hover syncs it, click selects. Shows the doc title (or the
 * path when untitled) over a `repo · path` subline.
 */
function IdleOption({
  index,
  active,
  title,
  repo,
  path,
  onActivate,
  onSelect,
}: {
  index: number;
  active: boolean;
  title: string | null;
  repo: string;
  path: string;
  onActivate: () => void;
  onSelect: () => void;
}) {
  return (
    <li
      id={optionId(index)}
      role="option"
      aria-selected={active}
      onMouseEnter={onActivate}
    >
      <button
        type="button"
        onClick={onSelect}
        className={`flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left ${
          active ? 'bg-accent-faint' : 'hover:bg-accent-faint'
        }`}
      >
        <span className="truncate text-sm text-text">{title ?? path}</span>
        <span className="truncate text-xs text-muted">
          {repo} · {path}
        </span>
      </button>
    </li>
  );
}
